import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) {
      navigate(user.role === "superadmin" ? "/super" : "/admin", { replace: true });
    }
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success("Willkommen zurück.");
      navigate(data.role === "superadmin" ? "/super" : "/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Anmeldung fehlgeschlagen.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-stone-50 grid place-items-center px-6 py-10">
      <div className="w-full max-w-md bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 md:p-10">
        <div className="w-12 h-12 rounded-xl bg-emerald-900 text-white grid place-items-center mb-5">
          <ShieldCheck className="w-6 h-6" />
        </div>
        <h1 className="font-display text-3xl font-semibold text-emerald-900">Anmelden</h1>
        <p className="text-stone-500 mt-1 mb-8">Praxis- oder Super-Admin-Zugang.</p>

        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <Label htmlFor="email" className="text-base font-medium text-stone-800">E-Mail</Label>
            <Input id="email" data-testid="login-email" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} className="h-14 px-4 text-lg rounded-xl mt-2" autoComplete="email" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-base font-medium text-stone-800">Passwort</Label>
              <Link to="/passwort-vergessen" data-testid="link-forgot" className="text-sm text-emerald-800 hover:underline">Passwort vergessen?</Link>
            </div>
            <Input id="password" data-testid="login-password" type="password" required value={password}
              onChange={(e) => setPassword(e.target.value)} className="h-14 px-4 text-lg rounded-xl mt-2" autoComplete="current-password" />
          </div>
          <Button type="submit" data-testid="login-submit" disabled={loading}
            className="w-full h-16 text-lg rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
            <LogIn className="mr-2 w-5 h-5" /> {loading ? "Anmelden…" : "Anmelden"}
          </Button>
        </form>

        <p className="mt-6 text-sm text-stone-500 text-center">
          Neue Praxis? <Link to="/testzugang" data-testid="link-register" className="text-emerald-900 font-medium hover:underline">Testzugang anfordern</Link>
        </p>
        <p className="mt-2 text-xs text-stone-400 text-center">
          Patiententablet? <Link to="/tablet" data-testid="link-tablet" className="text-stone-500 hover:underline">Hier zum Tablet-Login</Link>
        </p>
      </div>
    </div>
  );
}
